import { heureAParis, instantDeParis, jourAParis } from './calendrier'
import { formaterJour } from './dates'
import type { DeclencheurCollecte, EtatConnexionPortail, StatutCollecte } from './domaine'
import { VERSION_REGLE_JUSTIFICATIFS } from './justificatifs'
import {
  ACTIONS_TACHES_JUSTIFICATIFS,
  HEURE_PASSAGE_MENSUEL,
  JOUR_PASSAGE_MENSUEL,
} from './justificatifs-portail'
import { formaterPeriode } from './periode'
import { MINUTES_AVANT_SUITE, STATUTS_COLLECTE_OUVERTS } from './portails'

// Collecteurs à la demande (décision de Pierre-Marie du 21/10/2026) : plus aucune collecte ne
// part seule, ni celle des portails de documents à leur fréquence, ni le passage mensuel des
// portails de justificatifs (coupe-circuit `portails.planification`, faux). Une personne lance
// chaque collecteur, depuis la page « Collecteurs » ou depuis la tâche qui l'attend ; la page met
// en avant ceux dont le lancement est nécessaire, pour ces raisons, dans cet ordre.

/**
 * La tâche d'un collecteur (décision de Pierre-Marie du 02/11/2026 : « une seule tâche de collecte
 * grand maximum par collecteur, et par défaut pas de tâche du tout sauf si c'est utile ; quand on
 * fait tourner le collecteur, on ferme les tâches associées sauf si échec ») : une par connexion
 * au plus, pour son titulaire, et seulement pour un geste qu'un humain seul peut faire
 * (`tacheCollecteur`). Ce que « Lancer » suffit à faire, la page « Collecteurs » le dit sans tâche.
 */
export const ACTION_TACHE_COLLECTEUR = 'collecteur'

/**
 * Les tâches de connexion d'avant le 02/11/2026 (vérification, liaison à un ordinateur,
 * justificatifs à choisir, à fournir, à vérifier) : la tâche du collecteur les remplace, elles
 * sont fermées à son premier recompte.
 */
export const ACTIONS_TACHES_CONNEXION_ANCIENNES = [
  'verifier_portail',
  'relier_portail',
  ...ACTIONS_TACHES_JUSTIFICATIFS,
] as const

/**
 * Tâches dont la fiche porte le bouton qui lance le collecteur : récupérer la pièce (le
 * fournisseur des mails ; ouverte, elle le met en avant, et son passage réussi la ferme) et la
 * tâche du collecteur (sa connexion).
 */
export const ACTIONS_TACHES_COLLECTEUR = ['recuperer_piece', ACTION_TACHE_COLLECTEUR] as const

/** Journal d'une tâche « récupérer la pièce » fermée par le passage réussi de son collecteur. */
export const JOURNAL_FERMEE_PAR_COLLECTE = 'fermee_par_collecte'

/**
 * Une tâche fermée par le passage de son collecteur reçoit encore, ce nombre de jours, la pièce que
 * ce passage a rapportée, lue après sa fin : elle rejoint le mail, sans rouvrir la tâche.
 */
export const JOURS_PIECE_APRES_FERMETURE = 2

/** Ce qu'une tâche de collecteur demande, dans l'ordre de son titre. */
export const PARTIES_TACHE_COLLECTEUR = ['reprise', 'relier', 'a_trancher', 'a_verifier'] as const
export type PartieTacheCollecteur = (typeof PARTIES_TACHE_COLLECTEUR)[number]

/** Connexion à reprendre : bloquée, ou vérification restée sans réponse. */
export type RepriseConnexion = 'bloquee' | 'verification'

/**
 * La connexion attend-elle qu'un humain la reprenne ? Bloquée (échecs répétés, blocage par le
 * portail) ; vérification requise une fois sa collecte close (défi resté sans réponse). Pendant
 * une collecte, le défi a son écran et sa notification : pas de tâche. Une erreur seule non
 * plus : la page « Collecteurs » la dit.
 */
export function repriseAttendue(c: {
  etat: EtatConnexionPortail
  collecteOuverte: boolean
}): RepriseConnexion | null {
  if (c.etat === 'bloquee') return 'bloquee'
  if (c.etat === 'verification_requise' && !c.collecteOuverte) return 'verification'
  return null
}

export type BesoinsCollecteur = {
  /** Libellé de la connexion : il ouvre le titre. */
  connexion: string
  reprise: RepriseConnexion | null
  /** Rien ne relève la connexion : pourquoi (`motifSansReleve`) ; sinon null. */
  sansReleve: string | null
  /** Portail de justificatifs : justificatifs à choisir (liste « À choisir » du bilan). */
  aTrancher: number
  /** Portail de justificatifs : dépôts de l'agent chez le cabinet à vérifier. */
  aVerifier: number
}

/**
 * La tâche du collecteur, ou null s'il n'y a rien qu'un humain seul puisse faire (elle se ferme).
 * Titre : « <connexion> : connexion bloquée, 2 justificatifs à choisir ». Les opérations sans
 * justificatif (« À fournir ») et les commandes sans facture n'en ouvrent pas : le bilan et la
 * page « Collecteurs » les disent (décision du 02/11/2026).
 */
export function tacheCollecteur(
  b: BesoinsCollecteur,
): { titre: string; parties: PartieTacheCollecteur[] } | null {
  const pluriel = (n: number, un: string, plusieurs: string) => `${n} ${n === 1 ? un : plusieurs}`
  const morceaux: [PartieTacheCollecteur, string][] = []
  if (b.reprise)
    morceaux.push([
      'reprise',
      b.reprise === 'bloquee' ? 'connexion bloquée' : 'vérification nécessaire',
    ])
  if (b.sansReleve) morceaux.push(['relier', 'à relier à un ordinateur'])
  if (b.aTrancher > 0)
    morceaux.push([
      'a_trancher',
      pluriel(b.aTrancher, 'justificatif à choisir', 'justificatifs à choisir'),
    ])
  if (b.aVerifier > 0)
    morceaux.push(['a_verifier', pluriel(b.aVerifier, 'dépôt à vérifier', 'dépôts à vérifier')])
  if (morceaux.length === 0) return null
  return {
    titre: `${b.connexion} : ${morceaux.map(([, t]) => t).join(', ')}`,
    parties: morceaux.map(([p]) => p),
  }
}

/**
 * Fermée ou annulée par un humain ce mois-ci (Paris), la tâche ne revient pas avant le mois
 * suivant (décision du 14/10/2026, étendue le 02/11/2026), sauf une collecte finie sans aboutir
 * depuis (« sauf si échec ») ou des dépôts à vérifier : des écritures de l'agent chez le cabinet,
 * qui reviennent tant qu'il en reste.
 */
export function tacheCollecteurMalgreFermeture(
  parties: readonly PartieTacheCollecteur[],
  apresEchec: boolean,
): boolean {
  return apresEchec || parties.includes('a_verifier')
}

/** Ce que chaque partie de la tâche demande, en tête de son paragraphe de la description. */
export const TEXTES_TACHE_COLLECTEUR: Readonly<Record<PartieTacheCollecteur, string>> = {
  reprise:
    'La connexion attend une intervention : se connecter ou valider soi-même (Réglages, Connexions, Portails), puis lancer le collecteur. Son passage réussi ferme la tâche.',
  relier: 'Rien ne relève la connexion : la relier à un ordinateur.',
  a_trancher:
    'Justificatifs que la règle ne choisit pas seule : dans le bilan, « C’est celle-ci » sur la bonne pièce (l’agent du Mac la relie au prochain passage ; le débit du relevé est rapproché du paiement prévu de la pièce quand il lui correspond), ou « Pas de justificatif attendu ». Relier à la main chez le cabinet reste possible : le passage suivant le reconnaît.',
  a_verifier:
    'Documents déposés chez le cabinet par l’agent sans liaison sûre (orphelins, dépôt interrompu, dépôt non relié depuis sept jours) : vérifier chez le cabinet, relier à la main, puis le noter dans l’appli.',
}

export const RAISONS_A_LANCER = [
  /** Une tâche « récupérer la pièce » ouverte l'attend (pas la tâche du collecteur lui-même). */
  'tache',
  /** Dernière collecte en échec, bloquée, expirée, ou qui attend une vérification. */
  'echec',
  /** Mise en file il y a plus d'une heure, pas encore prise (ordinateur éteint, Chrome fermé). */
  'en_file',
  /**
   * Portail de justificatifs, écriture ouverte : passage à blanc validé pour une autre version de
   * la règle, chaque passage reste à blanc jusqu'à la nouvelle validation.
   */
  'revalider',
  /** Portail de justificatifs, écriture ouverte : pièces prêtes à déposer chez le cabinet. */
  'depots',
  /** Portail de justificatifs : le passage du mois n'est pas fait. */
  'passage',
  /** Mois sans le document attendu (cadence connue). */
  'manquants',
  /** Facture d'une activité pas encore émise à la dernière collecte, il y a quelques jours. */
  'a_venir',
  /** La dernière collecte a laissé des documents au-delà de sa limite. */
  'suite',
  /** Dernière collecte réussie plus vieille que la fréquence de la connexion. */
  'frequence',
  /** Jamais collectée. */
  'jamais',
] as const
export type TypeRaisonALancer = (typeof RAISONS_A_LANCER)[number]

/** Ce qui met un collecteur en échec : sa dernière collecte, sinon l'état de la connexion. */
type StatutEnEchec =
  'echec' | 'bloquee' | 'expiree' | 'attente_humain' | 'erreur' | 'verification_requise'

export type RaisonALancer =
  | { type: 'tache'; nombre: number }
  | {
      type: 'echec'
      statut: StatutEnEchec
      le: Date | null
    }
  | { type: 'en_file'; depuis: Date }
  | { type: 'revalider'; versionValidee: number | null }
  | { type: 'depots'; nombre: number }
  | { type: 'passage'; mois: string }
  | { type: 'manquants'; mois: readonly string[] }
  | { type: 'a_venir'; mois: readonly string[]; vuLe: Date | null }
  | { type: 'suite' }
  | { type: 'frequence'; jours: number | null; frequenceJours: number }
  | { type: 'jamais' }

/** Une collecte en file depuis ce délai n'a pas été prise : elle est signalée. */
export const MINUTES_EN_FILE_SIGNALEE = 60

/**
 * Une facture à venir (activité pas encore facturée) remet le collecteur en avant ce nombre de
 * jours après la collecte qui l'a vue : le fournisseur la publie quelques jours après le mois.
 */
export const JOURS_AVANT_A_VENIR = 3

/** Ce qu'il faut savoir d'une connexion pour dire si son collecteur est à lancer. */
export type CollecteurAJuger = {
  active: boolean
  /** Un adaptateur relève la connexion ; faux : dépôt à la main seulement. */
  robot: boolean
  /** Portail de justificatifs (Tiime, Dougs) : un passage, pas des documents. */
  justificatifs: boolean
  etat: EtatConnexionPortail
  frequenceJours: number
  /** À partir de quand la connexion est due (sa fréquence écoulée). */
  prochaineCollecteLe: Date | null
  dernierSuccesLe: Date | null
  /** La dernière collecte de la connexion, ouverte ou close. */
  derniere: {
    statut: StatutCollecte
    declencheur: DeclencheurCollecte
    creeLe: Date
    finLe: Date | null
  } | null
  /** Mois sans le document attendu (`moisManquants`). */
  moisManquants: readonly string[]
  /** Mois dont la facture est à venir (`moisAttendus`), mis à part des manquants. */
  moisAVenir: readonly string[]
  /** Tâches « récupérer la pièce » ouvertes qui attendent ce collecteur. */
  taches: number
  /** Portail de justificatifs : fin du dernier passage complet ; null : aucun. */
  passageCompletLe: Date | null
  /**
   * Portail de justificatifs, écriture ouverte, passage à blanc validé pour une autre version de
   * la règle que celle en vigueur : sa version ; sinon null.
   */
  passageARevalider: { versionValidee: number | null } | null
  /** Portail de justificatifs, écriture ouverte : opérations sûres, pièce prête à déposer. */
  depots: number
}

/**
 * Le mois dont le passage de justificatifs est attendu (AAAA-MM) : le mois en cours à partir du
 * 2 à 9 h (Paris), le précédent avant.
 */
export function passageDuMoisAttendu(maintenant: Date): string {
  const jour = jourAParis(maintenant)
  const mois = jour.slice(0, 7)
  const debut = Date.parse(
    instantDeParis(
      `${mois}-${String(JOUR_PASSAGE_MENSUEL).padStart(2, '0')}`,
      HEURE_PASSAGE_MENSUEL,
    ),
  )
  if (maintenant.getTime() >= debut) return mois
  const [annee, m] = mois.split('-').map(Number)
  return m === 1 ? `${annee! - 1}-12` : `${annee}-${String(m! - 1).padStart(2, '0')}`
}

const STATUTS_EN_ECHEC = ['echec', 'bloquee', 'expiree'] as const
const ETATS_EN_ECHEC = ['erreur', 'bloquee', 'verification_requise'] as const
const estUn = <T extends string>(liste: readonly T[], x: string): x is T =>
  (liste as readonly string[]).includes(x)

/**
 * Pourquoi lancer ce collecteur, dans l'ordre de `RAISONS_A_LANCER` ; vide : rien à lancer.
 * Désactivée ou sans adaptateur : jamais. Une collecte en cours, ou en file depuis peu : rien,
 * elle tourne déjà. Un échec dit seul ce qui est dû : ni fréquence ni « jamais » en plus.
 */
export function raisonsALancer(c: CollecteurAJuger, maintenant: Date): RaisonALancer[] {
  if (!c.active || !c.robot) return []
  const d = c.derniere
  const ouverte = d !== null && STATUTS_COLLECTE_OUVERTS.includes(d.statut)
  const enFileSignalee =
    d?.statut === 'en_file' &&
    maintenant.getTime() - d.creeLe.getTime() >= MINUTES_EN_FILE_SIGNALEE * 60_000
  if (ouverte && d.statut !== 'attente_humain' && !enFileSignalee) return []

  const raisons: RaisonALancer[] = []
  if (c.taches > 0) raisons.push({ type: 'tache', nombre: c.taches })
  if (d?.statut === 'attente_humain')
    raisons.push({ type: 'echec', statut: 'attente_humain', le: d.creeLe })
  else if (enFileSignalee) raisons.push({ type: 'en_file', depuis: d.creeLe })
  else if (d && estUn(STATUTS_EN_ECHEC, d.statut))
    raisons.push({ type: 'echec', statut: d.statut, le: d.finLe ?? d.creeLe })
  else if (estUn(ETATS_EN_ECHEC, c.etat))
    raisons.push({ type: 'echec', statut: c.etat, le: d ? (d.finLe ?? d.creeLe) : null })
  const enEchec = raisons.some((r) => r.type === 'echec' || r.type === 'en_file')

  if (c.justificatifs) {
    // Validé pour une autre version : lancer ne fait que des passages à blanc, rien ne se dépose.
    if (c.passageARevalider)
      raisons.push({ type: 'revalider', versionValidee: c.passageARevalider.versionValidee })
    else if (c.depots > 0) raisons.push({ type: 'depots', nombre: c.depots })
    const mois = passageDuMoisAttendu(maintenant)
    if (c.passageCompletLe === null || jourAParis(c.passageCompletLe).slice(0, 7) < mois)
      raisons.push({ type: 'passage', mois })
    return raisons
  }

  if (c.moisManquants.length > 0) raisons.push({ type: 'manquants', mois: c.moisManquants })
  if (
    c.moisAVenir.length > 0 &&
    (c.dernierSuccesLe === null ||
      maintenant.getTime() - c.dernierSuccesLe.getTime() >= JOURS_AVANT_A_VENIR * 86_400_000)
  )
    raisons.push({ type: 'a_venir', mois: c.moisAVenir, vuLe: c.dernierSuccesLe })
  if (enEchec) return raisons
  if (c.etat === 'jamais' && d === null) raisons.push({ type: 'jamais' })
  else if (c.prochaineCollecteLe !== null && c.prochaineCollecteLe <= maintenant) {
    // Après un succès qui a laissé des documents, la connexion est due un quart d'heure plus
    // tard (`collecteSuivante`) : c'est la suite, pas la fréquence.
    const suite =
      c.dernierSuccesLe !== null &&
      c.prochaineCollecteLe.getTime() - c.dernierSuccesLe.getTime() <= MINUTES_AVANT_SUITE * 60_000
    raisons.push(
      suite
        ? { type: 'suite' }
        : {
            type: 'frequence',
            jours:
              c.dernierSuccesLe === null
                ? null
                : Math.floor((maintenant.getTime() - c.dernierSuccesLe.getTime()) / 86_400_000),
            frequenceJours: c.frequenceJours,
          },
    )
  }
  return raisons
}

/** Rang d'un collecteur sur la page : sa première raison ; aucune, après tous les autres. */
export function rangCollecteur(raisons: readonly RaisonALancer[]): number {
  if (raisons.length === 0) return RAISONS_A_LANCER.length
  return Math.min(...raisons.map((r) => RAISONS_A_LANCER.indexOf(r.type)))
}

const jourDe = (instant: Date) => formaterJour(jourAParis(instant))

const TEXTES_ECHEC: Record<StatutEnEchec, string> = {
  echec: 'Dernière collecte en échec',
  bloquee: 'Dernière collecte bloquée',
  expiree: 'Vérification restée sans réponse',
  attente_humain: 'Attend une vérification depuis',
  erreur: 'Dernière collecte en erreur',
  verification_requise: 'Vérification requise depuis',
}

/** La raison en une phrase, pour la page et la tâche. */
export function texteRaison(r: RaisonALancer): string {
  switch (r.type) {
    case 'tache':
      return r.nombre === 1 ? 'Une tâche l’attend' : `${r.nombre} tâches l’attendent`
    case 'echec':
      return r.le ? `${TEXTES_ECHEC[r.statut]} le ${jourDe(r.le)}` : TEXTES_ECHEC[r.statut]
    case 'en_file':
      return `En file depuis le ${jourDe(r.depuis)} à ${heureAParis(r.depuis)}, pas encore prise`
    case 'revalider':
      return `Passage à blanc à revalider dans le bilan (validé ${r.versionValidee === null ? 'pour une autre version' : `en version ${r.versionValidee}`} de la règle, en vigueur : ${VERSION_REGLE_JUSTIFICATIFS}) : d’ici là, chaque passage reste à blanc`
    case 'depots':
      return `${r.nombre} justificatif${r.nombre > 1 ? 's' : ''} prêt${r.nombre > 1 ? 's' : ''} à déposer chez le cabinet`
    case 'passage':
      return `Passage du mois pas encore fait (${formaterPeriode(r.mois)})`
    case 'manquants':
      return `Manque : ${r.mois.map(formaterPeriode).join(', ')}`
    case 'a_venir':
      return `Facture à venir : ${r.mois.map(formaterPeriode).join(', ')}${r.vuLe ? ` (pas encore émise le ${jourDe(r.vuLe)})` : ''}`
    case 'suite':
      return 'La dernière collecte a laissé des documents à relever'
    case 'frequence':
      return r.jours === null
        ? `Aucune collecte réussie (une tous les ${r.frequenceJours} jours)`
        : `Dernière collecte réussie il y a ${r.jours} jour${r.jours > 1 ? 's' : ''} (une tous les ${r.frequenceJours} jours)`
    case 'jamais':
      return 'Jamais collectée'
  }
}

// Ordinateur muet (relevé le 28/10/2026) : l'agent du Mac fait une collecte à la fois ; suspendu
// depuis le 18/10 sur la fermeture d'un onglet, il a laissé onze collectes en file sans que rien
// ne le dise. Il tient une collecte tant qu'il bat (toutes les 30 s) : des collectes de ses
// connexions qui attendent sans qu'il en tienne aucune disent un ordinateur éteint, un collecteur
// arrêté ou suspendu.

/** Des collectes en file depuis ce délai sans aucune collecte tenue : l'ordinateur est muet. */
export const MINUTES_ORDINATEUR_MUET = 10

export type OrdinateurMuet = {
  enAttente: number
  /** La plus ancienne des collectes en file. */
  enFileDepuis: Date
  /** Son dernier battement (ou sa dernière prise) ; null : il n'a jamais tenu de collecte. */
  derniereActiviteLe: Date | null
}

/**
 * Muet : une collecte de ses connexions attend en file depuis `MINUTES_ORDINATEUR_MUET` au moins,
 * et il n'a tenu aucune collecte depuis ce délai. Il en tient une (les autres attendent leur
 * tour), ou la file est récente : null.
 */
export function ordinateurMuet(
  o: { enFile: readonly Date[]; derniereActiviteLe: Date | null },
  maintenant: Date,
): OrdinateurMuet | null {
  if (o.enFile.length === 0) return null
  const seuil = maintenant.getTime() - MINUTES_ORDINATEUR_MUET * 60_000
  const enFileDepuis = new Date(Math.min(...o.enFile.map((d) => d.getTime())))
  if (enFileDepuis.getTime() > seuil) return null
  if (o.derniereActiviteLe !== null && o.derniereActiviteLe.getTime() > seuil) return null
  return { enAttente: o.enFile.length, enFileDepuis, derniereActiviteLe: o.derniereActiviteLe }
}

/** L'ordinateur muet en une phrase, pour la page « Collecteurs ». */
export function texteOrdinateurMuet(libelle: string, m: OrdinateurMuet): string {
  const tenue = m.derniereActiviteLe
    ? `dernière collecte tenue le ${jourDe(m.derniereActiviteLe)} à ${heureAParis(m.derniereActiviteLe)}`
    : 'aucune collecte tenue jusqu’ici'
  return `« ${libelle} » ne prend plus les collectes : ${m.enAttente} en file depuis le ${jourDe(m.enFileDepuis)} à ${heureAParis(m.enFileDepuis)}, ${tenue}.`
}

/**
 * Une collecte ouverte qu'un lancement reprend au lieu d'être refusé : planifiée (sans personne)
 * et encore en file, ou arrêtée avant l'envoi d'un code (défi qui n'est pas en direct).
 */
export function lancementReprend(o: {
  collecte: { statut: StatutCollecte; declencheur: DeclencheurCollecte } | null
  defi: { enDirect: boolean } | null
}): boolean {
  if (!o.collecte) return false
  if (o.collecte.statut === 'en_file' && o.collecte.declencheur === 'planifie') return true
  return o.defi !== null && !o.defi.enDirect
}

/**
 * « Lancer » (« Rapprocher maintenant » pour un portail de justificatifs) : connexion active,
 * relevée par un adaptateur, rien qui empêche sa relève (`sansReleve`), et aucune collecte
 * ouverte, sauf celle qu'un lancement reprend. Le serveur décide du reste (passage à blanc pour
 * une autre personne que le titulaire).
 */
export function lancementPossible(c: {
  active: boolean
  robot: boolean
  sansReleve: string | null
  collecte: { statut: StatutCollecte; declencheur: DeclencheurCollecte } | null
  defi: { enDirect: boolean } | null
}): boolean {
  return c.active && c.robot && !c.sansReleve && (!c.collecte || lancementReprend(c))
}
